// src/contexts/AuthContext.js
import { createContext, useContext, useEffect, useState } from "react";
import { onAuthStateChanged, getIdTokenResult } from "firebase/auth";
import { auth, db } from "../firebase";
import { doc, getDoc } from "firebase/firestore";

const AuthContext = createContext({
  user: null,
  isAdmin: false,
  loading: true,
});

async function fetchAdminEmails() {
  try {
    const snap = await getDoc(doc(db, "data", "admin"));
    const data = snap.data() || {};
    return Array.isArray(data.email)
      ? data.email.map((e) => e.toLowerCase())
      : [];
  } catch (e) {
    console.error("Failed to fetch admin emails:", e);
    return [];
  }
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let unsubscribe;

    // IIFE로 async 처리
    (async () => {
      // 1) Firestore에서 관리자 이메일 로드
      const adminEmails = await fetchAdminEmails();

      // 2) 이제 Auth 상태 구독
      unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
        setUser(currentUser);

        if (!currentUser) {
          setIsAdmin(false);
        } else {
          const email = currentUser.email?.toLowerCase() || "";

          // Firestore 목록으로 판별
          const byEmailList = adminEmails.includes(email);

          // 커스텀 클레임(admin:true)
          let byClaim = false;
          try {
            const idTokenResult = await getIdTokenResult(currentUser, true);
            byClaim = !!idTokenResult.claims.admin;
          } catch {
            /* ignore */
          }

          setIsAdmin(byEmailList || byClaim);
        }

        // 최초 로딩 끝
        setLoading(false);
      });
    })();

    return () => {
      if (unsubscribe) unsubscribe();
    };
  }, []);

  // 로딩이 끝나기 전엔 자식 컴포넌트를 렌더링하지 않습니다.
  if (loading) return null;

  return (
    <AuthContext.Provider value={{ user, isAdmin, loading }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
